// Meta Pixel helpers. All calls are no-ops if fbq isn't loaded or marketing
// consent isn't granted. Consent is enforced at consent-grant time via
// `fbq('consent', 'grant'|'revoke')` in CookieConsent — these helpers also
// re-read localStorage as a safety net for edge cases (script loaded but
// consent not yet propagated to the pixel script).

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
  }
}

const hasMarketingConsent = (): boolean => {
  try {
    const raw = localStorage.getItem('cookieConsent');
    if (!raw) return false;
    return JSON.parse(raw)?.marketing === true;
  } catch {
    return false;
  }
};

export const fbqTrack = (
  event: string,
  params?: Record<string, any>,
  options?: { eventID?: string }
): void => {
  if (typeof window === 'undefined' || !window.fbq) return;
  if (!hasMarketingConsent()) return;
  if (options?.eventID) {
    window.fbq('track', event, params || {}, { eventID: options.eventID });
  } else {
    window.fbq('track', event, params || {});
  }
};

export const fbqGrantConsent = (): void => {
  if (typeof window === 'undefined' || !window.fbq) return;
  window.fbq('consent', 'grant');
};

export const fbqRevokeConsent = (): void => {
  if (typeof window === 'undefined' || !window.fbq) return;
  window.fbq('consent', 'revoke');
};

// Convenience event helpers — keep params minimal and safe to call from anywhere

export const trackPageView = () => fbqTrack('PageView');

export const trackViewContent = (params: {
  content_ids: string[];
  content_name?: string;
  content_type?: string;
  value?: number;
  currency?: string;
}) => fbqTrack('ViewContent', { content_type: 'product', ...params });

export const trackAddToCart = (params: {
  content_ids: string[];
  content_name?: string;
  content_type?: string;
  value?: number;
  currency?: string;
  contents?: Array<{ id: string; quantity: number }>;
}) => fbqTrack('AddToCart', { content_type: 'product', ...params });

export const trackInitiateCheckout = (params: {
  content_ids?: string[];
  num_items?: number;
  value?: number;
  currency?: string;
  contents?: Array<{ id: string; quantity: number }>;
}) => fbqTrack('InitiateCheckout', { content_type: 'product', ...params });

export const trackPurchase = (
  params: {
    content_ids: string[];
    contents?: Array<{ id: string; quantity: number }>;
    num_items?: number;
    value: number;
    currency: string;
  },
  eventID?: string
) => fbqTrack('Purchase', { content_type: 'product', ...params }, { eventID });
